import * as React from 'react';
import { Activity, CheckCircle2, XCircle, Clock } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './Card';
import { Badge } from './Badge';
import { cn } from '../utils/cn';
import { getRelativeTime, formatDuration } from '../utils';
import type { Session, SessionStatus } from '../types';

export interface SessionCardProps {
  session: Session;
  onClick?: () => void;
  className?: string;
}

const statusConfig: Record<
  SessionStatus,
  { icon: React.ReactNode; variant: 'success' | 'destructive' | 'warning' | 'info'; label: string }
> = {
  RUNNING: {
    icon: <Activity className="h-3.5 w-3.5 animate-pulse" />,
    variant: 'info',
    label: 'Running',
  },
  COMPLETED: {
    icon: <CheckCircle2 className="h-3.5 w-3.5" />,
    variant: 'success',
    label: 'Completed',
  },
  FAILED: {
    icon: <XCircle className="h-3.5 w-3.5" />,
    variant: 'destructive',
    label: 'Failed',
  },
  PENDING: {
    icon: <Clock className="h-3.5 w-3.5" />,
    variant: 'warning',
    label: 'Pending',
  },
};

export const SessionCard = React.memo<SessionCardProps>(
  ({ session, onClick, className }) => {
    const config = statusConfig[session.status];
    const duration = session.ended_at
      ? new Date(session.ended_at).getTime() - new Date(session.started_at).getTime()
      : Date.now() - new Date(session.started_at).getTime();

    return (
      <Card
        className={cn(
          'transition-all hover:shadow-md',
          onClick && 'cursor-pointer hover:border-primary/50',
          className
        )}
        onClick={onClick}
      >
        <CardHeader>
          <div className="flex items-start justify-between">
            <div className="space-y-1">
              <CardTitle className="text-base">{session.agent_name}</CardTitle>
              <CardDescription className="text-xs">
                {session.workflow_type}
              </CardDescription>
            </div>
            <Badge variant={config.variant} className="flex items-center gap-1">
              {config.icon}
              <span>{config.label}</span>
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-2 text-sm">
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Started</span>
              <span className="font-medium text-foreground">
                {getRelativeTime(session.started_at)}
              </span>
            </div>
            <div className="flex items-center justify-between text-muted-foreground">
              <span>Duration</span>
              <span className="font-medium text-foreground">
                {formatDuration(duration)}
              </span>
            </div>
            {session.progress !== undefined && session.status === 'RUNNING' && (
              <div className="space-y-1">
                <div className="flex items-center justify-between text-muted-foreground">
                  <span>Progress</span>
                  <span className="font-medium text-foreground">
                    {Math.round(session.progress * 100)}%
                  </span>
                </div>
                <div className="h-2 w-full overflow-hidden rounded-full bg-secondary">
                  <div
                    className="h-full bg-primary transition-all duration-300"
                    style={{ width: `${session.progress * 100}%` }}
                  />
                </div>
              </div>
            )}
            {session.result && session.status === 'COMPLETED' && (
              <div className="mt-2 rounded-md bg-muted p-2 text-xs text-muted-foreground">
                {session.result}
              </div>
            )}
            {session.result && session.status === 'FAILED' && (
              <div className="mt-2 rounded-md bg-destructive/10 p-2 text-xs text-destructive">
                {session.result}
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }
);

SessionCard.displayName = 'SessionCard';
